import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PartyPopper, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  city: "",
  state: "",
  why: "",
  for_whom: "me",
  bundle_offer: "yes",
  hear_about: "instagram",
  foot_choice: "both",
};

const RadioField = ({ id, label, value, onChange, options }) => (
  <div className="space-y-3">
    <Label className="font-jbmono text-xs uppercase tracking-widest text-cream/70">{label}</Label>
    <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-3" data-testid={id}>
      {options.map((o) => (
        <label
          key={o.value}
          className={`flex items-center gap-2 border rounded-sm px-4 py-2.5 cursor-pointer text-sm transition-colors duration-200 ${
            value === o.value ? "border-crimson bg-crimson/10 text-cream" : "border-steel/60 text-cream/60 hover:border-steel"
          }`}
        >
          <RadioGroupItem value={o.value} data-testid={`${id}-${o.value}`} />
          {o.label}
        </label>
      ))}
    </RadioGroup>
  </div>
);

export const PreOrderModal = ({ open, onOpenChange, defaultFoot }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, foot_choice: defaultFoot || "both" }));
  }, [open, defaultFoot]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.city.trim() || !form.state.trim()) {
      toast.error("Name, city and state are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm(initial);
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="preorder-modal"
        data-lenis-prevent
        className="bg-midnight border-steel text-cream max-w-lg max-h-[85vh] overflow-y-auto overscroll-contain"
      >
        {done ? (
          <div className="py-10 flex flex-col items-center text-center gap-4" data-testid="preorder-success">
            <PartyPopper className="w-12 h-12 text-gold" />
            <h3 className="font-display font-black uppercase text-3xl tracking-tight">You're on the list!</h3>
            <p className="text-cream/70 text-sm leading-relaxed max-w-sm">
              Your Master Blaster pre-order is locked in. We'll reach out before the Christmas launch — keep one foot ready.
            </p>
            <button
              onClick={() => close(false)}
              data-testid="preorder-success-close"
              className="mt-4 bg-crimson hover:bg-crimsondark text-white font-display font-bold uppercase tracking-wide px-8 py-3 rounded-sm transition-colors duration-200"
            >
              Back to the Revolution
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-black uppercase text-2xl tracking-tight">
                Pre-Order Your <span className="text-crimson">Master Blaster</span>
              </DialogTitle>
              <DialogDescription className="text-cream/60 text-sm">
                Reserve yours today — pay nothing until the official Christmas launch.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5 mt-2" data-testid="preorder-form">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">Full Name</Label>
                <Input
                  id="full_name"
                  data-testid="lead-input-full-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Crazy Neighbor Edward"
                  className="bg-navy border-steel text-cream placeholder:text-cream/30"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">City</Label>
                  <Input
                    id="city"
                    data-testid="lead-input-city"
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Eagleville"
                    className="bg-navy border-steel text-cream placeholder:text-cream/30"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">State</Label>
                  <Input
                    id="state"
                    data-testid="lead-input-state"
                    value={form.state}
                    onChange={set("state")}
                    placeholder="TX"
                    className="bg-navy border-steel text-cream placeholder:text-cream/30"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="why" className="font-jbmono text-xs uppercase tracking-widest text-cream/70">
                  Why do you want a Master Blaster?
                </Label>
                <Textarea
                  id="why"
                  data-testid="lead-input-why"
                  value={form.why}
                  onChange={set("why")}
                  placeholder="Tell us your story…"
                  className="bg-navy border-steel text-cream placeholder:text-cream/30 min-h-[90px]"
                />
              </div>
              <RadioField
                id="lead-radio-for-whom"
                label="Is this for you or a gift?"
                value={form.for_whom}
                onChange={set("for_whom")}
                options={[
                  { value: "me", label: "For me" },
                  { value: "gift", label: "It's a gift" },
                ]}
              />
              <RadioField
                id="lead-radio-foot"
                label="Which foot are we blasting?"
                value={form.foot_choice}
                onChange={set("foot_choice")}
                options={[
                  { value: "left", label: "Left foot" },
                  { value: "right", label: "Right foot" },
                  { value: "both", label: "Both feet" },
                ]}
              />
              <RadioField
                id="lead-radio-bundle"
                label="Do you want the Buy 3 Get 1 FREE offer?"
                value={form.bundle_offer}
                onChange={set("bundle_offer")}
                options={[
                  { value: "yes", label: "Yes — Freedom Pack" },
                  { value: "no", label: "No thanks" },
                ]}
              />
              <RadioField
                id="lead-radio-hear"
                label="How did you hear about us?"
                value={form.hear_about}
                onChange={set("hear_about")}
                options={[
                  { value: "instagram", label: "Instagram" },
                  { value: "tiktok", label: "TikTok" },
                  { value: "friend", label: "Friend / Family" },
                  { value: "google", label: "Google" },
                  { value: "other", label: "Other" },
                ]}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-form-submit"
                className="w-full flex items-center justify-center gap-2 bg-crimson hover:bg-crimsondark disabled:opacity-60 text-white font-display font-black uppercase tracking-wide text-lg px-6 py-4 rounded-sm transition-colors duration-200"
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {submitting ? "Locking It In…" : "Reserve My Master Blaster"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
